import { CafCorpusAction, CafFinalResponseStance } from '../../api/cafCorpus';

/** Every staff-facing word for the AI Curation page lives here (Ryan: plain words, no codes). */

export const FINAL_RESPONSE_QUESTION = 'Is this a final response?';
/** Final review C4: one set of words, everywhere, for a final response the AI
 *  couldn't prove. Curation names the group with the label and explains it with
 *  the words; the Lender lane's "Final response?" badge uses the words as its title. */
export const FINAL_RESPONSE_CHECK_LABEL = 'Final response check needed';
export const FINAL_RESPONSE_CHECK_WORDS = 'AI thinks this is a final response — please confirm';
export const NEEDS_FINAL_RESPONSE_SETTING = 'Needs a final-response setting';
export const FINAL_RESPONSE_COPY: Record<CafFinalResponseStance, string> = {
  final_response: 'Final response',
  not_final_response: 'Not a final response',
};
export const finalResponseWords = (value: string | null | undefined): string =>
  value === 'final_response' || value === 'not_final_response' ? FINAL_RESPONSE_COPY[value] : 'Not set yet';

export type PillTone = 'teal' | 'blue' | 'amber' | 'grey';

export interface ActionCopy {
  /** What happens, as an instruction ("Attach and mark done"). */
  label: string;
  /** What the AI did, past tense ("Attached and marked done"). */
  did: string;
  tone: PillTone;
  emailsClient: boolean;
  explain: string;
}

export const ACTION_COPY: Record<CafCorpusAction, ActionCopy> = {
  AUTO_ASSIGN_COMPLETE: {
    label: 'Attach and mark done', did: 'Attached and marked done', tone: 'teal', emailsClient: false,
    explain: 'Attach the email to the claim it quotes, then mark the task done. Nothing is sent to the client.',
  },
  AUTO_ASSIGN_ONLY: {
    label: 'Attach only', did: 'Attached, left open', tone: 'teal', emailsClient: false,
    explain: 'Attach the email to the claim and leave the task open for a person.',
  },
  AUTO_ASSIGN_SEND_LOA: {
    label: 'Attach and send LOA', did: 'Attached, LOA sent', tone: 'teal', emailsClient: true,
    explain: 'Attach the email, then email the client a letter of authority to sign.',
  },
  AUTO_ASSIGN_MULTI_REP: {
    label: 'Attach and send multi-rep email', did: 'Attached, multi-rep email sent', tone: 'teal', emailsClient: true,
    explain: 'Attach the email, then ask the client to confirm who represents them.',
  },
  AUTO_ASSIGN_CANCEL_CONFIRM: {
    label: 'Attach and reaffirm authority', did: 'Attached, reaffirmation requested', tone: 'teal', emailsClient: true,
    explain: 'Attach the email, then ask the client to reaffirm our authority to act.',
  },
  AUTO_ARCHIVE: {
    label: 'Archive', did: 'Archived', tone: 'blue', emailsClient: false,
    explain: 'Not about a claim, or nothing to do. The email is archived and nobody has to read it.',
  },
  MANUAL_REVIEW: {
    label: 'Mark as manual', did: 'Marked as manual', tone: 'amber', emailsClient: false,
    explain: 'Leave it for a person to read and decide.',
  },
};

export const ACTION_ORDER: CafCorpusAction[] = [
  'AUTO_ASSIGN_COMPLETE', 'AUTO_ASSIGN_ONLY', 'AUTO_ASSIGN_SEND_LOA', 'AUTO_ASSIGN_MULTI_REP',
  'AUTO_ASSIGN_CANCEL_CONFIRM', 'AUTO_ARCHIVE', 'MANUAL_REVIEW',
];

const SOMETHING_ELSE: ActionCopy = {
  label: 'Something else', did: 'Something else', tone: 'grey', emailsClient: false, explain: '',
};

export const actionCopy = (action: string | null | undefined): ActionCopy =>
  action && Object.prototype.hasOwnProperty.call(ACTION_COPY, action)
    ? ACTION_COPY[action as CafCorpusAction]
    : SOMETHING_ELSE;

export type HoldGroupKey =
  | 'final' | 'finalcheck' | 'noclaim' | 'noexample' | 'unsure' | 'cancelled' | 'attachment' | 'aitrouble' | 'other';

/** Spec 2026-10-08 §4.12: a confirmed final response and one the AI couldn't
 *  prove are separate groups (they used to share "always checked by staff"). */
export const HOLD_GROUPS: ReadonlyArray<{ key: HoldGroupKey; label: string; sentence: string }> = [
  // List-neutral (final review C8): an older backend has no lists.
  { key: 'final', label: 'Final response', sentence: 'It is a final response, so staff check it.' },
  { key: 'finalcheck', label: FINAL_RESPONSE_CHECK_LABEL, sentence: `${FINAL_RESPONSE_CHECK_WORDS}.` },
  { key: 'noclaim', label: "Couldn't find the claim", sentence: "The email couldn't be matched to one of our claims." },
  { key: 'noexample', label: 'No approved example for this sender', sentence: 'There is no approved example for this sender yet.' },
  { key: 'unsure', label: "AI wasn't sure enough to act alone", sentence: "The AI wasn't sure enough to act alone." },
  { key: 'cancelled', label: 'Claim is cancelled or closed', sentence: 'The claim is cancelled or closed.' },
  { key: 'attachment', label: 'Attachment needs a person', sentence: 'The attachment needs a person to read it.' },
  { key: 'aitrouble', label: "The AI couldn't give an answer", sentence: "The AI couldn't give an answer this time." },
  { key: 'other', label: 'Other reasons', sentence: 'A house rule marked it as manual.' },
];

/** effective_decision.hold_reason values (caf_task_classifier, caf_fenced_executor). */
const HOLD_CODE_GROUP: Record<string, HoldGroupKey> = {
  current_final_response: 'final',
  final_response_uncertain: 'finalcheck',
  // P2 safety net (spec §4.2): the examples said "not a final response" but the
  // letter says it is one, or part of the letter couldn't be read.
  final_response_example_conflict: 'finalcheck',
  final_response_example_unreadable: 'finalcheck',
  claim_match_absent: 'noclaim',
  claim_match_failed: 'noclaim',
  claim_match_ambiguous: 'noclaim',
  claim_reference_conflict: 'noclaim',
  rag_identity_ambiguous: 'noclaim',
  rag_identity_unverified: 'noclaim',
  rag_guidance_not_supplied: 'noexample',
  rag_no_suitable_match: 'noexample',
  cancelled_claim_review: 'cancelled',
  cancellation_state_disagreement: 'cancelled',
  password_protected: 'attachment',
  attachment_requires_review: 'attachment',
  rag_model_unavailable: 'aitrouble',
  rag_technical_failure: 'aitrouble',
  rag_unreadable: 'aitrouble',
  rag_invalid_model_output: 'aitrouble',
};

/** Codes whose own sentence says more than their group's. */
const HOLD_CODE_SENTENCE: Record<string, string> = {
  final_response_example_conflict: 'The examples said this is not a final response, but the email says it is.',
  final_response_example_unreadable: "The examples said this is not a final response, but part of the letter couldn't be read.",
};

const own = (table: Record<string, string>, code: string): boolean =>
  Object.prototype.hasOwnProperty.call(table, code);

/** Composite codes (a fixed prefix plus a free-form suffix, e.g. an exception
 *  class or provider error string) always fold to "Other reasons" -- the
 *  suffix is never plain enough to show, and never worth naming a group for. */
const OTHER_PREFIXES = ['execution_failed:', 'intake_observation_failed:'];

/** No reason recorded on a staff-bound decision means the AI itself chose a person. */
export const holdGroup = (code: string | null | undefined): HoldGroupKey => {
  if (!code || code === 'unknown') return 'unsure';
  if (OTHER_PREFIXES.some((prefix) => code.startsWith(prefix))) return 'other';
  return own(HOLD_CODE_GROUP, code) ? HOLD_CODE_GROUP[code] : 'other';
};

const groupOf = (code: string | null | undefined) =>
  HOLD_GROUPS.find((group) => group.key === holdGroup(code)) ?? HOLD_GROUPS[HOLD_GROUPS.length - 1];

export const holdLabel = (code: string | null | undefined): string => groupOf(code).label;
export const holdSentence = (code: string | null | undefined): string =>
  (code && own(HOLD_CODE_SENTENCE, code) ? HOLD_CODE_SENTENCE[code] : groupOf(code).sentence);

const RULE_SENTENCES: Record<string, string> = {
  cancelled_claim_routing: 'The claim is cancelled or closed.',
  cancelled_state_guard: 'The claim is cancelled or closed.',
  staff_final_response: 'A person had already said whether this is a final response.',
  category_override: 'The approved example sets the classification.',
  archive_reference_override: 'The approved example allows archiving emails that quote a reference.',
  multi_rep_precedent: 'The approved example says another firm may be acting.',
  model_output_validation: "The AI's answer couldn't be used.",
  // caf_task_classifier._current_identity: identity details (claim reference, names, agreement
  // numbers) the model gave are kept only when the current email's own text contains them.
  current_message_identity: 'Details the AI gave, like the claim reference, can only be used when this email itself contains them.',
  // The final-response assessment is re-checked against the current email's own evidence.
  current_final_response: 'Whether this is a final response was checked against this email itself.',
  example_finality: 'Approved examples from this sender decided whether this is a final response.',
  example_safety_net: 'The examples said it is not a final response, but the email itself needs checking.',
};

export const ruleSentence = (effect: Record<string, unknown>): string => {
  const rule = typeof effect.rule === 'string' ? effect.rule : '';
  if (rule === 'mandatory_retrieval') {
    return holdSentence(typeof effect.reason_code === 'string' ? effect.reason_code : null);
  }
  return RULE_SENTENCES[rule] ?? 'A house rule changed it.';
};

export type Verdict = 'correct' | 'incorrect' | 'unsure';

export const VERDICT_COPY: Record<Verdict, { label: string; className: string; icon: 'check' | 'x' | 'question' }> = {
  correct: { label: 'Right', className: 'cur-v-right', icon: 'check' },
  incorrect: { label: 'Wrong', className: 'cur-v-wrong', icon: 'x' },
  unsure: { label: 'Not sure', className: 'cur-v-unsure', icon: 'question' },
};

export const FIRST_LABEL_REASON = 'First label';
export const REPLACED_LABEL_REASON = 'Replaced an earlier label';
